// ===============================
// Gift for Her — flow engine
//
// Duplicated from assets/jewellery-find.js and adapted for the Gift Finder API
// contract, which is a plain recursive drill-down (every level is fetched the
// same way; a card's own navigation.screenName says whether to go deeper or
// stop), rather than the jewellery flow's single-fetch-then-wizzy-filter shape.
// There is no PLP-filter facet step and no wizzy-filter POST here, so that
// machinery (FILTER_KEY_MAP, wizzyFilterSearch, updateShopifyCards, the
// JewelleryFinderHandle branch) is intentionally not present. The jewellery
// file's `isReverse` plumbing was also left out: it is threaded through
// firstApiCall()/updateDetails() there but never actually invoked with `true`
// (Previous works purely through history.back() + restoreState()), so it is
// dead weight this rewrite does not need to carry.
// ===============================

const getPrevBtn = () => document.querySelector('.Previous-btn');

// Every question renders into the SAME .find-content-box-main - there is no
// navigation between steps, so the scroll offset simply carries over. Instant,
// not smooth: a smooth window scroll on every render fights the entrance
// animation. Anchored to .mainFinderView rather than a hardcoded 0 so it stays
// correct if anything is ever rendered above the wizard.
const gfScrollToStepTop = () => {
  const view = document.querySelector('.mainFinderView');
  const top = view ? window.pageYOffset + view.getBoundingClientRect().top : 0;
  window.scrollTo({ top: Math.max(0, top), left: 0, behavior: 'auto' });
};

// ===============================
// Offline fallback (bundled snapshot)
// The middleware CORS allowlist only accepts kushals.com, so preview/draft
// theme origins (*.shopifypreview.com) get a 403 on every call. Load the
// snapshot asset lazily — only once a live call has actually failed.
// ===============================
let fallbackPromise = null;
function loadFallback() {
  if (window.GF_FALLBACK) return Promise.resolve(window.GF_FALLBACK);
  if (!window.gfFallbackUrl) return Promise.resolve(null);
  if (!fallbackPromise) {
    fallbackPromise = new Promise((resolve) => {
      const s = document.createElement('script');
      s.src = window.gfFallbackUrl;
      s.onload = () => resolve(window.GF_FALLBACK || null);
      s.onerror = () => resolve(null);
      document.head.appendChild(s);
    });
  }
  return fallbackPromise;
}

// ===============================
// CleverTap — Gift Finder funnel
//
// Every GF event lives here rather than in clevertap_custom.js.liquid, because
// they all need finder state (which question is on screen, which tiles were
// picked) that only this file holds. The CTA on the landing pages still records
// the entry page there, since that is the only place that knows it.
// ===============================
const GF_ENTRY_PAGE_KEY = 'gf_entry_page';

// CleverTap: both finders fire the same generic event names (finder_started,
// finder_q{n}_clicked, ...). This `feature` param is what tells them apart.
const GF_FEATURE = 'Gift Finder';

// Written on the collection hand-off, read once by assets/custom.js on the PLP
// to fire visited_plp_from_gift_finder. Shared with jewellery-find.js — the
// `feature` field inside says which finder wrote it.
const FINDER_VISITED_PLP_KEY = 'finder_visited_plp_params';

// The question the customer left from on the way to the collection page, kept
// outside the history stack so the trip back can rebuild it — see
// gfRestoreAfterCollection().
const GF_PLP_STATE_KEY = 'gf_plp_return_state';
// Ignore a stored view older than this, so a marker left behind by a finished
// run cannot hijack a reload much later in the same tab.
const GF_PLP_STATE_MAX_AGE = 30 * 60 * 1000;

// Landing-page chip. The Jewellery Finder lands with filter.p.m.custom.product
// in the URL (the type the customer picked), so the PLP+ chip rail renders that
// chip selected. The Gift Finder never asks for a type, so to land with the same
// selected look it asks the collection for its chip values in rail order
// (templates/collection.gf-chips.liquid) and appends the FIRST one — the rail's
// first chip is on and the grid narrows to that type, exactly as if the
// customer had tapped it. A slow or failed lookup just lands without it.
const GF_CHIP_PARAM = 'filter.p.m.custom.product';
const GF_CHIP_VIEW = 'gf-chips';
const GF_CHIP_FETCH_TIMEOUT = 1500;
// Stagger between finder_q3_clicked and finder_preferences so CleverTap (second
// resolution) stamps preferences on a later second — see gfGoToCollection().
const GF_PREFERENCES_DELAY = 1000;

// STOPGAP (2026-10-15) — crop table for the API's step-2/3 card artwork.
// Every PNG holds the same 789×996 card body, but each export crops the
// shadow bleed differently, so the body sits at a different offset and the
// cards never line up in the grid. The host (api.technoboost.in/uploads)
// sends no CORS header, so a <canvas> cannot read the pixels to find the body
// itself; canvas size alone cannot tell 830-wide (L0) from 831-wide (L40).
// Offsets measured with a PNG decoder on 2026-10-15. Keyed by filename stem
// (the hash suffix is stripped). A file not listed here just renders
// `contain`, uncropped. Real fix: design re-exports body-only PNGs, or the
// host adds `Access-Control-Allow-Origin` so this can auto-detect.
const GF_ART_BODY = { w: 789, h: 996 };
const GF_ART_CROP = {
  Frame_48100648: { w: 830, h: 1052, l: 0,  t: 0  },   // Birthday
  Frame_48101108: { w: 831, h: 1052, l: 40, t: 0  },   // Anniversary
  Frame_48100650: { w: 831, h: 1076, l: 40, t: 24 },   // Casual Gifting
  Component_279:  { w: 870, h: 1076, l: 40, t: 24 },   // budget tiers (all six
  Component_280:  { w: 870, h: 1076, l: 40, t: 24 },   // measured identical)
  Component_281:  { w: 870, h: 1076, l: 40, t: 24 },
};
// Inline style that scales the PNG so its card body fills a clipping
// wrapper (.img / .gf-art-crop). Empty string when the file is unknown.
function gfArtCropStyle(url) {
  const m = /\/([A-Za-z]+_\d+)(?:_\d+)*_[0-9a-f]+\.png(?:\?.*)?$/.exec(url || '');
  const c = m && GF_ART_CROP[m[1]];
  if (!c) return '';
  const pct = (n, d) => (100 * n / d).toFixed(3) + '%';
  return 'position:absolute;left:-' + pct(c.l, GF_ART_BODY.w) + ';top:-' + pct(c.t, GF_ART_BODY.h) +
    ';width:' + pct(c.w, GF_ART_BODY.w) + ';height:' + pct(c.h, GF_ART_BODY.h) +
    ';max-width:none;object-fit:fill;';
}

// The 3-level drill-down is Recipient -> Occasion -> Budget.
const GF_PARAM_BY_QUESTION = {
  1: 'Gift_Recipient',
  2: 'Gift_Occasion',
  3: 'Gift_Budget',
};

// { 1: ['Mom'], 2: ['Birthday'], 3: ['Under 499'] }
let gfAnswers = {};
// One "Started" per view of the finder. The start button is auto-clicked on load
// (sections/gift-for-her-find.liquid), and a tap on it can follow, which would
// otherwise report the funnel entry twice.
let gfStarted = false;
console.log('[KUSHALS-DEBUG] gf boot — navType:', (performance.getEntriesByType('navigation')[0] || {}).type, '| href:', location.href, '| history.state.api:', history.state && history.state.currentApi, '| persistedBfcache:', false);

// Exit reporting state. gfGoToCollection() is the completion path, so the
// navigation it schedules must never be counted as an exit —
// gfHandingOffToCollection marks that window. gfExitReported keeps it to one
// Exit per run however the customer leaves: the top back button, a link out, a
// back gesture off the page, or the tab closing.
let gfHandingOffToCollection = false;
let gfExitReported = false;

// clevertap loads async and is absent on a draft/preview origin, so every push
// below is gated on this rather than assuming the global is there.
const ctReady = () => typeof clevertap !== 'undefined' && !!clevertap?.event?.push;

// Where the customer entered the finder from. clevertap_custom.js.liquid writes
// this on the CTA click; a direct landing has nothing stored, so fall back to
// naming the referrer.
function gfEntryPage() {
  try {
    const stored = sessionStorage.getItem(GF_ENTRY_PAGE_KEY);
    if (stored) return stored;
  } catch (e) {}

  const referrer = document.referrer || '';
  if (!referrer) return 'Direct';
  try {
    const url = new URL(referrer);
    if (url.origin !== window.location.origin) return 'External';
    if (url.pathname === '/') return 'Homepage';
    if (url.pathname.startsWith('/products/')) return 'PDP';
    if (url.pathname.startsWith('/collections/')) return 'PLP';
    return url.pathname;
  } catch (e) {
    return 'Direct';
  }
}

// The question currently on screen. updateCards() bumps currentIndex after
// rendering, so it always runs one ahead of the question.
const gfCurrentQuestion = () => Math.max(currentIndex - 1, 1);

function gfRecordAnswer(questionNumber, tileNames) {
  const clean = (tileNames || [])
    .map((name) => String(name == null ? '' : name).trim())
    .filter(Boolean);
  if (!clean.length) return [];
  gfAnswers[questionNumber] = clean;
  return clean;
}

// The preference set built so far, as the three drill-down params. Steps the
// customer has not reached yet are left out rather than sent empty.
function gfPreferenceParams() {
  const params = {};
  Object.keys(GF_PARAM_BY_QUESTION).forEach((question) => {
    const values = gfAnswers[question] || [];
    if (values.length) params[GF_PARAM_BY_QUESTION[question]] = values.join(', ');
  });
  return params;
}

// Fires Q{n}_Clicked for the question just answered and remembers the answer for
// the preference event at the end of the flow.
function gfTrackQuestionAnswered(tileNames) {
  const questionNumber = gfCurrentQuestion();
  const answered = gfRecordAnswer(questionNumber, tileNames);
  if (!answered.length) return;

  // A deep-link replay (gfDeepLink) clicks tiles on the customer's behalf —
  // the answer is kept for the Preferences event, but nobody tapped a tile.
  console.log('[KUSHALS-DEBUG] gfTrackQuestionAnswered — Q:', questionNumber, '| tiles:', answered, '| ctReady:', ctReady(), '| deepLinking:', !!window.gfDeepLinking, '| t:', Date.now());
  if (ctReady() && !window.gfDeepLinking) {
    const params = { feature: GF_FEATURE, "Tile Name": answered.join(", ") };
    // Q2 onwards also carries the answer of the question before it.
    if (questionNumber > 1) {
      params.Previous_Selected_Tile = (gfAnswers[questionNumber - 1] || []).join(", ");
    }
    clevertap.event.push(`finder_q${questionNumber}_clicked`, params);
  }
}

// End of the flow: report the full preference set, then the collection hand-off,
// then navigate.
function gfGoToCollection(collectionHandle, priceCeiling) {
  // Completion, not abandonment — suppress the exit report for the navigation
  // scheduled at the end of this function.
  gfHandingOffToCollection = true;
  const preferences = gfPreferenceParams();
  console.log('[KUSHALS-DEBUG] gfGoToCollection — prefs:', JSON.stringify(preferences), '| ctReady:', ctReady(), '| t:', Date.now());

  // One param per question, whatever the question asked (recipient, occasion,
  // budget) — the sheet names them by position, not by meaning.
  const tiles = {
    feature: GF_FEATURE,
    Selected_Tile_Q1: (gfAnswers[1] || []).join(", "),
    Selected_Tile_Q2: (gfAnswers[2] || []).join(", "),
    Selected_Tile_Q3: (gfAnswers[3] || []).join(", ")
  };

  // finder_q3_clicked is pushed synchronously just before this call. CleverTap
  // stamps events to the second, so a same-tick push shares Q3's second and the
  // dashboard orders the pair arbitrarily (preferences showed above Q3 on
  // 2026-10-15). A 1s hold always lands on a later second, so preferences
  // reads after Q3 — the navigation hold below waits for this push.
  const preferencesPushed = new Promise((resolve) => {
    setTimeout(() => {
      if (ctReady()) {
        clevertap.event.push("finder_preferences", tiles);
      }
      resolve();
    }, GF_PREFERENCES_DELAY);
  });

  // Picked up by assets/custom.js on the collection page, which adds screen_url
  // and fires visited_plp_from_gift_finder. Stored even when clevertap is not
  // ready here — the PLP checks readiness itself.
  try {
    sessionStorage.setItem(FINDER_VISITED_PLP_KEY, JSON.stringify(tiles));
  } catch (e) {}

  // The history entry pushed for the last question does not survive this
  // hand-off — a back tap on the collection page lands on the previous
  // question's entry instead. Keep the view here, outside the stack, so the
  // return trip can rebuild it whatever the browser did.
  try {
    sessionStorage.setItem(GF_PLP_STATE_KEY, JSON.stringify({
      savedAt: Date.now(),
      state: buildState(true)
    }));
  } catch (e) {}

  // text1 is the API's price ceiling. Some entries send the literal string
  // "null" rather than JSON null (see gfCardLabel() below for the matching
  // convention on text3) — that means "no price cap", not "cap of NaN".
  const hasPrice = priceCeiling && priceCeiling !== 'null' && !isNaN(Number(priceCeiling));
  const params = new URLSearchParams();
  if (hasPrice) params.append('filter.v.price.lte', priceCeiling);

  // Hold = the 1s preferences stagger above + 500ms flush margin so that push
  // leaves the page before unload; the "getting your gifts ready" line covers
  // it. The collection page's own load keeps the loader on screen after this
  // anyway (the old 2.5s stacked dead time on a real wait). Do not drop the
  // 500ms margin. The chip lookup runs alongside, capped at
  // GF_CHIP_FETCH_TIMEOUT, so the hand-off waits for the slower of the two.
  const hold = preferencesPushed.then(() => new Promise((resolve) => setTimeout(resolve, 500)));
  Promise.all([hold, gfCollectionChipValues(collectionHandle)]).then(([, chipValues]) => {
    if (chipValues.length) params.append(GF_CHIP_PARAM, chipValues[0]);
    const query = params.toString();
    console.log('[KUSHALS-DEBUG] gfGoToCollection — chips:', JSON.stringify(chipValues), '| query:', query, '| t:', Date.now());
    window.location.href = `/collections/${encodeURIComponent(collectionHandle)}${query ? `?${query}` : ''}`;
  });
}

// The product-type chip values the landing collection renders, in rail order
// (first = highest count), from the ?view=gf-chips alternate template. Empty
// array on any failure or timeout — the hand-off must never be blocked by this.
async function gfCollectionChipValues(collectionHandle) {
  const controller = typeof AbortController === 'function' ? new AbortController() : null;
  const timer = setTimeout(() => { if (controller) controller.abort(); }, GF_CHIP_FETCH_TIMEOUT);
  try {
    const url = `/collections/${encodeURIComponent(collectionHandle)}?view=${GF_CHIP_VIEW}`;
    const response = await fetch(url, { credentials: 'same-origin', signal: controller ? controller.signal : undefined });
    if (!response.ok) {
      console.log('[KUSHALS-DEBUG] gfCollectionChipValues — status:', response.status, '| url:', url);
      return [];
    }
    const data = await response.json();
    const values = Array.isArray(data?.values) ? data.values : [];
    return values.filter((value) => typeof value === 'string' && value.trim() !== '');
  } catch (e) {
    console.log('[KUSHALS-DEBUG] gfCollectionChipValues — failed:', e && e.name, e && e.message);
    return [];
  } finally {
    clearTimeout(timer);
  }
}

// ===============================
// Global State
// ===============================
let currentApi = "gift-finder";
let currentIndex = 1;
let currentTileName = [];

// ===============================
// URL hash <-> finder step
//
// Every history entry the finder writes carries the step it shows as
// `#handle=<static-page key>` (e.g. /pages/gift-for-her#handle=gift-finder-for-mom),
// so the address bar always names the question on screen and the URL can be
// shared. Landing on a URL that already carries a non-root handle replays the
// path to it from the root level — see gfDeepLink() below.
// ===============================
const GF_ROOT_API = "gift-finder";
const GF_HASH_KEY = "handle";

function gfHandleFromHash() {
  const match = new RegExp(`[#&]${GF_HASH_KEY}=([^&]*)`).exec(window.location.hash || "");
  if (!match) return "";
  try {
    return decodeURIComponent(match[1]).trim();
  } catch (e) {
    return "";
  }
}

// Same path + query, only the hash swapped — history.*State() with a URL
// argument never scrolls or fires hashchange, so this is purely cosmetic to
// the rest of the page.
function gfStepUrl(api) {
  return `${window.location.pathname}${window.location.search}#${GF_HASH_KEY}=${encodeURIComponent(api || GF_ROOT_API)}`;
}

// ===============================
// History state saving / restoring
// ===============================
function buildState(isPush = false) {
  return {
    isGiftFinderState: true,
    currentIndex,
    currentApi,
    currentTileName: [...(currentTileName || [])],
    // Carried so the preference / back-button events still name the right tiles
    // after a back-navigation rebuilds the step from history.
    gfAnswers: JSON.parse(JSON.stringify(gfAnswers || {})),

    // DOM contents
    contentBoxMainHtml: jQuery(".find-content-box-main").html() || "",
    contentHeaderHtml: jQuery(".find-content-header").prop('outerHTML') || "",
    breadcrumbsHtml: (() => {
      const clone = jQuery(".kf-breadcrumbs").clone();
      clone.find(".kf-crumb").css("visibility", "");
      if (!isPush) {
        const maxCrumbs = currentIndex - 2;
        clone.find(".kf-crumb").each(function (idx) {
          if (idx >= maxCrumbs) {
            jQuery(this).remove();
          }
        });
      }
      return clone.html() || "";
    })(),
    progressUlHtml: jQuery(".rd_progress_main_ul").html() || "",

    // Element states
    nextBtnClassList: Array.from(document.querySelector('.Next-btn')?.classList || []),
    prevBtnStyleVisibility: document.querySelector('.Previous-btn')?.style.visibility || 'hidden',
    step1Display: 'none',
    step2Display: 'block',
    step3Display: 'none',

    // Checked inputs indices
    checkedIndices: Array.from(document.querySelectorAll(".find-content-box-main input")).map((inp, idx) => inp.checked ? idx : null).filter(idx => idx !== null)
  };
}

function saveState(isPush = false) {
  const state = buildState(isPush);

  try {
    if (isPush) {
      history.pushState(state, '', gfStepUrl(currentApi));
    } else {
      history.replaceState(state, '', gfStepUrl(currentApi));
      if (!window.gfInitialFinderState) {
        window.gfInitialFinderState = state;
      }
    }
  } catch (e) {
    console.error('[gift-for-her-find] Error writing history state:', e);
  }
}

function restoreState(state) {
  if (!state) return;
  currentIndex = state.currentIndex;
  currentApi = state.currentApi;
  currentTileName = state.currentTileName || [];
  gfAnswers = state.gfAnswers || {};

  // popstate already restored this entry's own URL; the initial-state fallback
  // (a null-state entry) and a reload have nothing to restore from, so set it.
  try {
    history.replaceState(state, '', gfStepUrl(currentApi));
  } catch (e) {}

  // Restore HTML
  if (state.contentBoxMainHtml !== undefined) {
    jQuery(".find-content-box-main").html(state.contentBoxMainHtml);
  }
  if (state.contentHeaderHtml) {
    jQuery(".find-content-header").replaceWith(state.contentHeaderHtml);
  }
  if (state.breadcrumbsHtml !== undefined) {
    jQuery(".kf-breadcrumbs").html(state.breadcrumbsHtml);
    jQuery(".kf-breadcrumbs .kf-crumb").css("visibility", "");
  }
  if (state.progressUlHtml !== undefined) {
    jQuery(".rd_progress_main_ul").html(state.progressUlHtml);
  }

  // Restore button states
  const nextBtn = document.querySelector('.Next-btn');
  if (nextBtn && state.nextBtnClassList) {
    nextBtn.className = 'btn Next-btn';
    state.nextBtnClassList.forEach(cls => {
      if (cls && cls !== 'btn' && cls !== 'Next-btn') {
        nextBtn.classList.add(cls);
      }
    });
  }
  const prevBtn = document.querySelector('.Previous-btn');
  if (prevBtn) {
    // saveState() runs AFTER updateCards() bumps currentIndex, so a restored
    // state carries the one-ahead value: Q1 is currentIndex 2, not 1.
    prevBtn.style.visibility = (gfCurrentQuestion() === 1) ? 'hidden' : 'visible';
  }

  // Restore step sections display
  jQuery('.find_step1').css('display', state.step1Display || 'none');
  // '' (not 'block') so the stylesheet's display:flex column wins — inline
  // block would flatten the step and drop the banner/footer back mid-page.
  jQuery('.find_step2').css('display', state.step2Display === 'none' ? 'none' : '');
  jQuery('.find_step3').css('display', state.step3Display || 'none');

  // Restore container class list (ensure main view is displayed and not hidden by inview)
  const mainView = document.querySelector('.mainFinderView');
  if (mainView) {
    mainView.classList.add('mainFinderView', 'kf-enter', 'kf-settled');
    mainView.classList.remove('kf-await-inview');
  }

  // Restore checked input states
  if (state.checkedIndices) {
    const inputs = document.querySelectorAll(".find-content-box-main input");
    inputs.forEach((inp, idx) => {
      inp.checked = state.checkedIndices.includes(idx);
      jQuery(inp).closest('.find_check_option').toggleClass('selected', inp.checked);
    });
  }

  // Remove deck/summary elements
  jQuery('.kf-deck').remove();
  jQuery('.kf-summary').remove();
}

// ===============================
// Returning from the collection page
//
// gfGoToCollection() stores the question it left from because the entry it was
// pushed under is gone by the time the customer taps back — the browser hands
// us the entry BEFORE it. When we land short like that, push the stored view so
// the customer sees the question they actually left, and a further back still
// walks the steps behind it.
// ===============================
function gfReadReturnState() {
  let raw;
  try {
    raw = sessionStorage.getItem(GF_PLP_STATE_KEY);
  } catch (e) {
    return null;
  }
  if (!raw) return null;

  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch (e) {
    parsed = null;
  }
  const state = parsed && parsed.state;
  if (!state || !state.isGiftFinderState) return null;
  if (!parsed.savedAt || (Date.now() - parsed.savedAt) > GF_PLP_STATE_MAX_AGE) return null;
  return state;
}

function gfClearReturnState() {
  try {
    sessionStorage.removeItem(GF_PLP_STATE_KEY);
  } catch (e) {}
}

function gfRestoreAfterCollection() {
  const stored = gfReadReturnState();
  if (!stored) return false;

  // Only when this is a step back INTO the flow. A fresh landing on the page
  // carries no finder state and must start at Q1 as usual — and must not eat
  // the stored view, which a later back tap may still need.
  const landed = history.state;
  if (!landed || !landed.isGiftFinderState) return false;

  gfClearReturnState();

  // The browser kept the right entry — restoreState() on it does the job.
  if ((landed.currentIndex || 0) >= (stored.currentIndex || 0)) return false;

  try {
    history.pushState(stored, '', gfStepUrl(stored.currentApi));
  } catch (e) {
    console.error('[gift-for-her-find] Error writing history state:', e);
  }
  restoreState(stored);
  return true;
}

// Every way back into a finder page: first paint, bfcache restore, popstate.
function gfEnterFromHistory() {
  const hasFinderState = !!(history.state && history.state.isGiftFinderState);
  if (!hasFinderState && !window.gfInitialFinderState) return false;

  // Back in the questionnaire, so a later abandonment is reportable again.
  gfHandingOffToCollection = false;
  gfExitReported = false;

  window.isRestoringState = true;
  if (!gfRestoreAfterCollection()) {
    if (hasFinderState) {
      restoreState(history.state);
    } else {
      restoreState(window.gfInitialFinderState);
    }
  }
  setTimeout(() => {
    window.isRestoringState = false;
  }, 0);
  return true;
}

// ===============================
// Event: Start button (delegated)
// ===============================
jQuery(document).on("click", ".start_find_btn_submit", () => {
  // A new run invalidates any view stored for a previous collection hand-off.
  gfClearReturnState();
  gfHandingOffToCollection = false;
  gfExitReported = false;

  if (!gfStarted) {
    gfStarted = true;
    gfAnswers = {};
    if (ctReady()) {
      console.log('[KUSHALS-DEBUG] Started push — entry:', gfEntryPage(), '| t:', Date.now());
      clevertap.event.push("finder_started", {
        feature: GF_FEATURE,
        page_name: gfEntryPage()
      });
    }
  }

  // A URL that already names a step (#handle=<key>) opens on that step. Once
  // per page load: a later tap on Start (after Previous-ing back out) is a
  // normal restart from the root level.
  const deepLinkTarget = gfHandleFromHash();
  if (deepLinkTarget && deepLinkTarget !== GF_ROOT_API && !gfDeepLinkDone) {
    gfDeepLinkDone = true;
    gfDeepLink(deepLinkTarget);
    return;
  }

  if (typeof firstApiCall === 'function') {
    firstApiCall(currentApi);
  }
});

// ===============================
// Claiming the forward history entry
//
// See assets/jewellery-find.js for the full iOS back-swipe-snapshot rationale
// this mirrors: the entry is claimed on the capture phase, before the deck
// overlay (appended by sections/gift-for-her-find.liquid on the bubble phase)
// paints, so a back-swipe snapshot is the question being left, not the tile
// animation.
// ===============================
let gfForwardClaimPending = false;

document.addEventListener('click', function (event) {
  const btn = event.target && event.target.closest && event.target.closest('.Next-btn');
  // The terminal step hands off straight to a collection page rather than to
  // another question, so it has no entry to claim.
  if (!btn || btn.classList.contains('final-call')) return;
  if (window.isRestoringState || gfForwardClaimPending) return;

  saveState(false);
  try {
    // Carries the step being left; updateCards() -> saveState(false) swaps in
    // the new step's handle once it has rendered.
    history.pushState(buildState(true), '', gfStepUrl(currentApi));
    console.log('[KUSHALS-DEBUG] Next capture — pushState url:', gfStepUrl(currentApi), '| history.length:', history.length);
    gfForwardClaimPending = true;
  } catch (e) {
    console.error('[gift-for-her-find] Error writing history state:', e);
  }
}, true);

// ===============================
// Event: Next button (delegated)
// ===============================
jQuery(document).on("click", ".Next-btn", async function () {
  console.log('[KUSHALS-DEBUG] Next-btn click — finalCall:', this.classList.contains('final-call'), '| currentIndex:', currentIndex, '| currentApi:', currentApi, '| href:', location.href, '| t:', Date.now());
  if (this.classList.contains('final-call')) {
    saveState(false);
  }

  const previousBtn = getPrevBtn();
  if (previousBtn) previousBtn.style.visibility = 'visible';

  const selectedRadio = jQuery(".find_check_option [name='find_radio_btn']:checked");
  const selectedSearchString = selectedRadio.data('searchstring');
  const selectedScreenName = selectedRadio.data('screenname');
  const selectedPrice = selectedRadio.data('text1');
  // trackname = analytics value (bare tier on the budget step); tilename is
  // the display label and stays for crumb / deck.
  currentTileName.push(selectedRadio.data('trackname') || selectedRadio.data('tilename'));

  if (currentIndex >= 2 && currentTileName.length) {
    gfTrackQuestionAnswered(currentTileName);
    currentTileName.length = 0;
  }

  // "ShopifyHandle" is terminal: stop drilling and hand off to the collection.
  // "StaticPage" advances one more level of the recursion.
  if (selectedScreenName === 'ShopifyHandle' && selectedSearchString) {
    gfGoToCollection(selectedSearchString, selectedPrice);
    return;
  }

  if (selectedScreenName === 'StaticPage' && selectedSearchString) {
    currentApi = selectedSearchString;
    await firstApiCall(currentApi);
    return;
  }

  // No navigable selection on this tile — nothing further to render.
});

// ===============================
// Event: Previous button (delegated)
//
// The in-flow Previous steps BACK one question. Back_Click is the question being
// left, Clicks_Lands_On the one it returns to, and Previous_Selected_Tile the
// answer already stored for that earlier question.
// ===============================
jQuery(document).on("click", ".Previous-btn", function (e) {
  e.preventDefault();

  const leaving = gfCurrentQuestion();
  const landingOn = Math.max(leaving - 1, 1);
  console.log('[KUSHALS-DEBUG] Previous-btn — leaving Q', leaving, '→ Q', landingOn, '| history.length:', history.length, '| href:', location.href);

  if (ctReady()) {
    clevertap.event.push("finder_back_button_click", {
      feature: GF_FEATURE,
      Back_Click: `Q${leaving}`,
      Previous_Selected_Tile: (gfAnswers[landingOn] || []).join(", "),
      Clicks_Lands_On: `Q${landingOn}`
    });
  }

  history.back();
});

// ===============================
// Event: top back button — exits the finder entirely (delegated)
//
// Distinct from Previous above: this one leaves the flow rather than stepping
// within it, so it reports which question the customer walked away from.
//
// NOTE: the finder page (templates/page.gift-finder-for-her.json) has no top
// back button of its own yet. The selectors below cover the theme's existing
// top bar and a [data-gf-exit] hook, so the event starts reporting the moment
// that button is added — nothing here needs to change then.
// ===============================
function gfInQuestionnaire() {
  const step2 = document.querySelector('.find_step2');
  if (!step2) return false;
  return getComputedStyle(step2).display !== 'none';
}

// One Exit per run, from whichever path notices the customer leaving first.
function gfReportExit() {
  console.log('[KUSHALS-DEBUG] gfReportExit — exitReported:', gfExitReported, '| handingOff:', gfHandingOffToCollection, '| inQuestionnaire:', gfInQuestionnaire(), '| Q:', gfCurrentQuestion(), '| ctReady:', ctReady(), '| native:', !!window.KushalsNativeCleverTap, '| t:', Date.now());
  if (gfExitReported || gfHandingOffToCollection) return false;
  if (!gfInQuestionnaire()) return false;

  gfExitReported = true;
  const payload = { feature: GF_FEATURE, Questionnaire: `Q${gfCurrentQuestion()}` };

  if (window.KushalsNativeCleverTap) {
    window.KushalsNativeCleverTap.pushEvent("finder_exit", JSON.stringify(payload));
  } else if (ctReady()) {
    clevertap.event.push("finder_exit", payload);
  }
  return true;
}

jQuery(document).on(
  "click",
  "[data-gf-exit], [data-k-plp-back], .k-plp-topbar__back",
  function (e) {
    if (!gfInQuestionnaire()) return;

    e.preventDefault();
    e.stopImmediatePropagation();

    gfReportExit();

    setTimeout(() => {
      window.location.href = '/';
    }, 100);
  }
);

// Any link that carries the customer off the finder page. Reported on the click
// rather than on unload, because CleverTap's queue is not guaranteed to flush
// once the page is tearing down. Anything inside the finder UI is in-flow
// navigation — tiles, Previous, the collection hand-off — and is skipped.
jQuery(document).on("click", "a[href]", function () {
  if (!gfInQuestionnaire()) return;
  if (this.closest('.find_step1, .find_step2')) return;

  const href = this.getAttribute('href') || '';
  if (!href || href.charAt(0) === '#') return;
  if (/^(javascript:|tel:|mailto:)/i.test(href)) return;

  gfReportExit();
});

// Backstop for the paths no click covers: the back gesture off the page, a
// typed URL, and closing the tab. pagehide rather than beforeunload, because
// iOS Safari skips beforeunload when a page goes into bfcache.
window.addEventListener('pagehide', function (event) {
  console.log('[KUSHALS-DEBUG] pagehide — persisted:', event.persisted, '| href:', location.href, '| t:', Date.now());
  gfReportExit();
});

// ===============================
// Main API Call
//
// Every level of the drill-down is fetched the same way: get the step's own
// title/description (col.filters[0]) to paint the header, then get its
// collectionItem list to paint the cards. Recursion depth is NOT hardcoded to
// 3 — the Next-btn handler above decides whether to go deeper or stop purely
// from each card's own navigation.screenName, so a 4th level added on the
// backend would work without a JS change.
// ===============================
// One level of the drill-down, live API first, bundled snapshot second.
// Shared by firstApiCall() (render) and gfFindPath() (deep-link lookup).
async function gfFetchLevelData(apiKey) {
  const url = `${window.devUrl}/callback/static-page/${apiKey}`;
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  } catch (apiError) {
    const fb = await loadFallback();
    const data = fb?.pages?.[apiKey];
    if (!data) throw apiError;
    console.warn('[gift-for-her-find] live API unavailable, using bundled snapshot for', apiKey);
    return data;
  }
}

async function firstApiCall(currentApiKey, isSecondCall = false) {
  try {
    const data = await gfFetchLevelData(currentApiKey);

    jQuery('.find_step1').hide();
    jQuery('.find_step2').show();

    const col = data?.[0]?.collections?.[0];
    if (!col) {
      // Some backend branches (e.g. Wife → Anniversary/Birthday) have no
      // content yet — show that plainly instead of freezing with no feedback.
      jQuery('.find-content-box-main').html(
        '<p class="gf-empty-state">No gifts found for this yet — try a different option.</p>'
      );
      document.querySelector('.Next-btn')?.classList.remove('final-call');
      return data;
    }

    if (!isSecondCall) {
      updateDetails(col.filters?.[0] || {});
    } else {
      const collectionItem = col.collectionItem || [];
      const isTerminal = collectionItem.length > 0 &&
        collectionItem.every((item) => item?.navigation?.screenName === 'ShopifyHandle');
      document.querySelector('.Next-btn')?.classList?.toggle('final-call', isTerminal);
      updateCards(collectionItem);
    }
    return data;
  } catch (error) {
    console.error('[gift-for-her-find] Error fetching data:', error);
    return null;
  }
}

// ===============================
// Deep link: #handle=<key> opens the finder on that step
//
// The wizard's state (progress rail, crumbs, history entries, answers for the
// Preferences event) is all built by the normal click flow, so rather than
// re-implementing it the replay drives that flow: render the root level, check
// the tile that leads towards the target, click Next, wait for the next level,
// repeat. Each replayed step therefore gets its own history entry exactly as a
// real tap would, so Previous/back from a deep-linked step still walk the
// earlier questions. The deck flight animation and the Q<N>_Clicked events are
// skipped while window.gfDeepLinking is set (the crumb is appended directly by
// sections/gift-for-her-find.liquid).
// ===============================
let gfDeepLinkDone = false;
const GF_DEEP_LINK_MAX_DEPTH = 3;
const GF_DEEP_LINK_STEP_TIMEOUT = 8000;

function gfLevelItems(data) {
  return data?.[0]?.collections?.[0]?.collectionItem || [];
}

// Chain of StaticPage keys from the root down to `target` (inclusive), or null
// when nothing reachable within GF_DEEP_LINK_MAX_DEPTH matches. Keys nest by
// prefix (gift-finder -> gift-finder-for-mom -> gift-finder-for-mom-birthday),
// so siblings that cannot be ancestors are only fetched when no prefix match
// exists at that level.
async function gfFindPath(target) {
  let frontier = [{ api: GF_ROOT_API, path: [] }];
  for (let depth = 0; depth < GF_DEEP_LINK_MAX_DEPTH && frontier.length; depth++) {
    const likely = frontier.filter((node) => target.startsWith(node.api));
    const candidates = likely.length ? likely : frontier;
    const levels = await Promise.all(candidates.map(async (node) => {
      try {
        return { node, items: gfLevelItems(await gfFetchLevelData(node.api)) };
      } catch (e) {
        return { node, items: [] };
      }
    }));
    const next = [];
    for (const { node, items } of levels) {
      for (const item of items) {
        const nav = item?.navigation || {};
        if (nav.screenName !== 'StaticPage' || !nav.searchString) continue;
        const path = node.path.concat(nav.searchString);
        if (nav.searchString === target) return path;
        next.push({ api: nav.searchString, path });
      }
    }
    frontier = next;
  }
  return null;
}

function gfWaitUntil(check, timeout = GF_DEEP_LINK_STEP_TIMEOUT) {
  return new Promise((resolve) => {
    const startedAt = Date.now();
    (function poll() {
      const result = check();
      if (result) return resolve(result);
      if (Date.now() - startedAt > timeout) return resolve(null);
      setTimeout(poll, 50);
    })();
  });
}

const gfStepSettled = (api) => () =>
  !gfForwardClaimPending &&
  !!(history.state && history.state.isGiftFinderState && history.state.currentApi === api);

async function gfDeepLink(target) {
  let path = null;
  try {
    path = await gfFindPath(target);
  } catch (e) {
    path = null;
  }
  if (!path) {
    console.warn('[gift-for-her-find] deep link handle not found, starting from the root level:', target);
    firstApiCall(currentApi);
    return;
  }

  window.gfDeepLinking = true;
  try {
    await firstApiCall(GF_ROOT_API);
    let api = GF_ROOT_API;
    for (const nextApi of path) {
      // The level has rendered once its tiles are in the DOM and its own
      // history entry is written (updateCards -> saveState).
      const ready = await gfWaitUntil(() => {
        if (!gfStepSettled(api)()) return null;
        return document.querySelector(
          `.find-content-box-main input[name="find_radio_btn"][data-searchstring="${CSS.escape(nextApi)}"]`
        );
      });
      if (!ready) {
        console.warn('[gift-for-her-find] deep link replay stalled at', api, 'looking for', nextApi);
        return;
      }
      ready.checked = true;
      jQuery(ready).closest('.find_check_option').addClass('selected');
      const nextBtn = document.querySelector('.Next-btn');
      if (!nextBtn) return;
      nextBtn.click();
      api = nextApi;
    }
    await gfWaitUntil(gfStepSettled(api));
  } finally {
    window.gfDeepLinking = false;
  }
}

// Figma (UI 264/268/272) question copy per step — keyed by currentIndex at
// the point updateDetails() runs (1 = Recipient, 2 = Occasion, 3 = Budget).
const GF_STEP_COPY = {
  1: { title: "Find a gift they’ll love", description: "Who are you gifting for?" },
  2: { title: "Find a gift they’ll love", description: "What’s The Special Occasion?" },
  3: { title: "Find a gift they’ll love", description: "What Would You Like To Spend?" },
};

// ===============================
// Update UI: header / progress step
// ===============================
const updateDetails = (filters) => {
  const updateHeader = (title, description) => {
    const questionTitle = `
      <div class="find-content-header">
        <p>${title || ""}</p>
        <h2>${description || ""}</h2>
      </div>`;
    jQuery(".find-content-header").replaceWith(questionTitle);
  };

  const stepElement = document.querySelector(`.rd_progress_${currentIndex}`);
  const stepLabel = document.querySelector(`.rd_progress_${currentIndex} p`);
  if (stepLabel && filters?.label) stepLabel.textContent = filters.label;
  stepElement?.classList?.add('active');

  // col.filters is always [] on every gift-finder endpoint (verified live on
  // all 3 levels) — the API has no question-copy field, so the banner text
  // per step is figma-fixed, not API-driven. Falls through to the API value
  // first in case the backend ever starts sending filters.
  const copy = (filters?.title || filters?.description) ? filters : (GF_STEP_COPY[currentIndex] || {});
  updateHeader(copy.title, copy.description);
  firstApiCall(currentApi, true);
};

// text3 is the API's display label, but it is currently sent as the literal
// string "null" (not JSON null) for some levels still being filled in on the
// backend — same "null" string convention the API uses for text1. name is
// always populated, if more verbose, so it is the fallback rather than an
// empty label.
function gfCardLabel(item) {
  if (item?.text3 && item.text3 !== 'null') return item.text3;
  if (item?.name && item.name !== 'null') return item.name;
  return '';
}

// Budget tiers under some occasions (e.g. mom-anniversary) send real JSON
// null for text1 even though the price is right there in name ("...under
// 499") — recovered from that field instead of falling back to gfCardLabel()
// (which would show "Mom"/text3, the price card's own broken-looking state).
function gfBudgetPrice(item) {
  if (item?.text1 && item.text1 !== 'null') return item.text1;
  const match = /(\d[\d,]*)/.exec(item?.name || '');
  return match ? match[1].replace(/,/g, '') : '';
}

// FALLBACK ONLY (since 2026-10-15): the API now ships full card artwork in
// images[0].url for occasion and budget cards, which updateCards() renders
// first. These local line-icon cards are used only when a card arrives
// without an image — the icon is picked by matching the API's own label text
// (falls back to the "gift" glyph for anything unrecognized).
const GF_OCCASION_ICONS = {
  birthday: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.3"><path d="M4 21v-8a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8H4Z"/><path d="M4 17c1.5 1 2.5 1 4 0s2.5-1 4 0 2.5 1 4 0 2.5-1 4 0"/><path d="M8 11V8M12 11V6M16 11V8"/><path d="M8 8c-.6 0-1-.6-1-1.5S8 5 8 5s1 .6 1 1.5S8.6 8 8 8ZM12 6c-.6 0-1-.6-1-1.5S12 3 12 3s1 .6 1 1.5S12.6 6 12 6ZM16 8c-.6 0-1-.6-1-1.5S16 5 16 5s1 .6 1 1.5S16.6 8 16 8Z"/></svg>',
  anniversary: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.3"><path d="M12 21s-7-4.4-9.5-8.8C.8 8.7 2.4 5 6 5c2 0 3.4 1.1 4 2.2C10.6 6.1 12 5 14 5c3.6 0 5.2 3.7 3.5 7.2C15 16.6 12 21 12 21Z"/><circle cx="12" cy="10" r="1.4"/><circle cx="9" cy="12.5" r="1.1"/><circle cx="15" cy="12.5" r="1.1"/></svg>',
  wedding: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.3"><circle cx="9" cy="14" r="5"/><circle cx="15" cy="14" r="5"/></svg>',
  gift: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.3"><rect x="4" y="9" width="16" height="11" rx="1"/><path d="M4 9h16M12 9v11"/><path d="M12 9c-1.5 0-4-1-4-3.2C8 4.2 9.2 3 10.6 3 12 3 12 6 12 9ZM12 9c1.5 0 4-1 4-3.2C16 4.2 14.8 3 13.4 3 12 3 12 6 12 9Z"/></svg>',
};
function gfOccasionIcon(label) {
  const key = Object.keys(GF_OCCASION_ICONS).find((k) => (label || '').toLowerCase().includes(k));
  return GF_OCCASION_ICONS[key || 'gift'];
}

// ===============================
// Renderer
// ===============================
const updateCards = (collectionCards) => {
  const step = currentIndex;
  const cardsHTML = (collectionCards || []).map((item) => {
    const label = gfCardLabel(item);
    const screenName = item?.navigation?.screenName || "";
    const searchString = item?.navigation?.searchString || "";
    const text1 = gfBudgetPrice(item);
    // The breadcrumb pill reads data-tilename (falling back to the card's own
    // h2 text) — the price card below has no h2, so its crumb label is
    // "Under ₹499" plain text, not the HTML-formatted priceText it displays.
    // text1 is a numeric ceiling ("499") or a tier name ("Luxe" on the top
    // card) — only a number reads as "Under ₹…".
    const isNumericPrice = text1 !== '' && !isNaN(Number(text1));
    const crumbLabel = (step === 3) ? (isNumericPrice ? `Under ₹${text1}` : (text1 || label)) : label;
    // data-tileName is the DISPLAY label (crumb / deck pill). data-trackName
    // is what analytics report: budget step sends the bare tier ("499",
    // "999", "Luxe"), never "Under ₹…"; other steps send the label.
    const trackName = (step === 3) ? (text1 || label) : label;
    const radio = `<input type="radio" name="find_radio_btn"
        data-screenName="${screenName}"
        data-searchString="${searchString}"
        data-tileName="${crumbLabel}"
        data-trackName="${trackName}"
        data-text1="${text1}">`;

    // The API now ships full card artwork (icon + copy, or "UNDER ₹499")
    // on occasion and budget cards. Render it as a photo card when present;
    // the local icon / price markup below stays as the fallback for any card
    // that arrives without one.
    const artUrl = item?.images?.[0]?.url || '';
    if ((step === 2 || step === 3) && artUrl) {
      const cropStyle = gfArtCropStyle(artUrl);
      return `
    <div class="find-content-box find_check_option gf-art-card">
      ${radio}
      <label>
        <div class="img${cropStyle ? ' gf-art-crop' : ''}">
          <img loading="eager" fetchpriority="high" src="${artUrl}" alt="${crumbLabel}"${cropStyle ? ` style="${cropStyle}"` : ''}>
        </div>
      </label>
    </div>
  `;
    }

    if (step === 2) {
      return `
    <div class="find-content-box find_check_option gf-icon-card">
      ${radio}
      <label>
        <div class="gf-icon">${gfOccasionIcon(label)}</div>
        <h2>${label}</h2>
      </label>
    </div>
  `;
    }

    if (step === 3) {
      const priceText = isNumericPrice
        ? `UNDER<br><span class="gf-price-value">₹${text1}</span>`
        : `<span class="gf-price-value">${text1 || label || ''}</span>`;
      return `
    <div class="find-content-box find_check_option gf-price-card">
      ${radio}
      <label>
        <p class="gf-price">${priceText}</p>
      </label>
    </div>
  `;
    }

    return `
    <div class="find-content-box find_check_option">
      ${radio}
      <label>
        <div class="img">
          <img loading="eager" fetchpriority="high" src="${item?.images?.[0]?.url || ""}" alt="${label}">
        </div>
        <h2>${label}</h2>
      </label>
    </div>
  `;
  }).join('');

  jQuery(".find-content-box-main").html(cardsHTML);
  gfScrollToStepTop();

  const prevBtn = document.querySelector('.Previous-btn');
  if (prevBtn) {
    prevBtn.style.visibility = (currentIndex === 1) ? 'hidden' : 'visible';
  }

  currentIndex++;

  // Fill in the entry the click already claimed with the question that just
  // rendered. Restores rebuild from a stored state and must not write back.
  if (!window.isRestoringState) {
    saveState(false);
    gfForwardClaimPending = false;
  }
};

// ===============================
// History event listeners & Init
// ===============================
window.addEventListener('popstate', function (event) {
  console.log('[KUSHALS-DEBUG] popstate — state.api:', event.state && event.state.currentApi, '| isGF:', !!(event.state && event.state.isGiftFinderState), '| href:', location.href, '| hasInitial:', !!window.gfInitialFinderState);
  if (event.state && event.state.isGiftFinderState) {
    window.isRestoringState = true;
    restoreState(event.state);
    setTimeout(() => {
      window.isRestoringState = false;
    }, 0);
  } else if (!event.state && window.gfInitialFinderState) {
    window.isRestoringState = true;
    restoreState(window.gfInitialFinderState);
    setTimeout(() => {
      window.isRestoringState = false;
    }, 0);
  }
});

window.addEventListener('pageshow', function (event) {
  console.log('[KUSHALS-DEBUG] pageshow — persisted:', event.persisted, '| href:', location.href);
  const s3 = document.querySelector('.find_step3');
  const isStep3Visible = s3 && getComputedStyle(s3).display !== 'none';
  if (event.persisted || isStep3Visible) {
    if (!gfEnterFromHistory()) {
      window.isRestoringState = true;
      jQuery('.find_step1').hide();
      jQuery('.find_step2').show();
      jQuery('.find_step3').hide();
      jQuery('.kf-summary').remove();
      jQuery('.kf-deck').remove();
      setTimeout(() => {
        window.isRestoringState = false;
      }, 0);
    }
  }
});

// A reload mid-flow keeps history.state; the section's auto-start skips when
// it sees one, so rebuild the step from it here (same as jewellery-find.js).
gfEnterFromHistory();
